<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert as wgptUserAlert } from 'dashboard/composables/wgpt';
import { useAlert } from 'dashboard/composables';
import { useEmitter } from 'dashboard/composables/emitter';

import SnackbarContainer from 'next/wgpt/Snackbar/SnackbarContainer.vue';
import Dialog from 'next/dialog/Dialog.vue';
import EditBotSourceChunkForm from './EditBotSourceChunkForm.vue';

const emit = defineEmits(['close']);
const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const dialogRef = ref(null);
const isOpen = ref(false);
const bot = ref({});
const chunk = ref({});
const uiFlags = getters['wgptBotSourceChunks/getUIFlags'];
const stack = ref([]);
const canDismiss = computed(() => stack.value.length === 0);

const source = computed(() => {
  return store.getters['wgptBotSources/getSource'](chunk.value.sourceId);
});

const onClose = () => {
  isOpen.value = false;
  emit('close');
};

const formRef = ref(null);

const open = (selectedBot, selectedChunk) => {
  bot.value = selectedBot;
  chunk.value = selectedChunk;
  isOpen.value = true;
  nextTick(() => {
    if (!dialogRef.value) return;
    dialogRef.value.open();
  });
};

const close = (force = false) => {
  if (!canDismiss.value && !force) return;
  dialogRef.value.close();
  onClose();
};

const onDialogClose = () => {
  if (!canDismiss.value) return;
  isOpen.value = false;
  emit('close');
};

useEmitter('WGPT_DROPDOWN_TOGGLE', ({ id, state }) => {
  if (state) {
    stack.value = [...stack.value, id];
  } else {
    stack.value = stack.value.filter(item => item !== id);
  }
});

const handleSubmit = async () => {
  try {
    if (!formRef.value) return;
    const { validate } = formRef.value;

    const data = await validate();
    if (!data) return;

    const botSourceChunk = {
      ...data,
      botId: bot.value.id,
      sourceId: chunk.value.sourceId,
      id: chunk.value.id,
    };
    await store.dispatch('wgptBotSourceChunks/update', botSourceChunk);

    useAlert(
      t(
        'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CHUNKS.EDIT.API.SUCCESS_MESSAGE'
      )
    );
    close();
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t(
        'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CHUNKS.EDIT.API.ERROR_MESSAGE'
      );
    wgptUserAlert(errorMessage);
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CHUNKS.EDIT.TITLE')
    "
    :confirm-button-label="
      t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CHUNKS.EDIT.SAVE')
    "
    :cancel-button-label="
      t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CHUNKS.EDIT.CANCEL')
    "
    :is-loading="uiFlags.isUpdating"
    :disable-confirm-button="uiFlags.isUpdating"
    :can-dismiss="canDismiss"
    @confirm="handleSubmit"
    @close="onDialogClose"
  >
    <SnackbarContainer class="-top-6 fixed -translate-y-full" />
    <EditBotSourceChunkForm
      v-if="isOpen"
      ref="formRef"
      :source="source"
      :chunk="chunk"
      @submit="handleSubmit"
    />
  </Dialog>
</template>
